import { Component, computed, effect, inject, signal, ChangeDetectionStrategy } from '@angular/core'
import { ActivatedRoute, Router } from '@angular/router'
import { Location } from '@angular/common'
import { Tag } from 'depoto-core/src/entities'
import { NotificationService } from '../../services'
import { AbstractControl, FormControl, FormGroup, Validators } from '@angular/forms'
import { CheckDataChanges } from '../../services/can-deactivate-guard.service'
import { TranslateService } from '@ngx-translate/core'

import { HttpClient } from '@angular/common/http'
import { injectMutation, injectQuery, injectQueryClient } from '@tanstack/angular-query-experimental'
import { TagService } from '../../services/entity-services/tag.service'
import { updateCachedListQueries } from '../../services/query-cache.service'
import { PayloadBuilderService } from '../../services/payload-builder.service'

// todo remove if not using
const tagSchema = {
  id: null,
  name: null,
  type: null,
  color: null,
  externalId: null,
  company: {
    id: null,
    name: null,
  },
  created: null,
  updated: null,
}

@Component({
  selector: 'tag',
  templateUrl: './tag.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class TagComponent implements CheckDataChanges {
  tagService = inject(TagService)
  private readonly http = inject(HttpClient)
  private queryClient = injectQueryClient()

  // public tag: Tag
  public tagTypes = []
  submitted = signal(false)

  // Keep a snapshot of the initial form values to detect unsaved changes
  private initialFormValue = signal<any>(null)

  tagForm = new FormGroup({
    name: new FormControl({ value: '', disabled: false }, [Validators.required]),
    type: new FormControl({ value: '', disabled: false }, [Validators.required]),
    color: new FormControl({ value: '', disabled: false }, []),
    externalId: new FormControl({ value: '', disabled: false }, []),
  })

  readonly queryTag = injectQuery(() => ({
    queryKey: ['tag', Number(this.route.snapshot.params['id'])],
    queryFn: async () => {
      const id = this.route.snapshot.params['id']
      if (id) {
        return await this.tagService.getTag(id)
      } else {
        return new Tag()
      }
    },
    onSuccess: data => {
      this.fillFormValue(data)
    },
    // enabled: !!this.router.url.split('/')[2],
  }))

  private payloadBuilder = inject(PayloadBuilderService)

  mutationCreateTag = injectMutation(() => ({
    mutationFn: async () => {
      this.submitted.set(true)
      if (this.tagForm.invalid) {
        throw new Error('Form is invalid')
        return
      }

      const current = this.tagForm.getRawValue() as any
      const initial = this.initialFormValue() as any
      const n = this.payloadBuilder.buildPatch(current, initial, {
        // no id on create
        required: ['name', 'type', 'color', 'externalId'],
        optional: {
          name: {},
          type: {},
          color: {},
          externalId: {},
        },
      })

      // Return the created tag so onSuccess receives the created entity (with id)
      return await this.tagService.createTag(n, tagSchema)
    },
    onSuccess: data => {
      this.submitted.set(false)
      // After successful create, snapshot current form as the new baseline to avoid false unsaved prompts
      const current = this.tagForm.getRawValue()
      this.initialFormValue.set(structuredClone(current))
      this.tagForm.markAsPristine()

      this.notificationService.success(this.translateService.instant('alert.tag.created'))

      // Update the detail cache for this tag so consumers see fresh data immediately
      this.queryClient.setQueryData(['tag', Number(data['id'])], data)

      // OPTIONS: keep as-is → update options cache so selects/filters see the new tag immediately
      updateCachedListQueries(this.queryClient, ['tags', 'options'], data, {
        mode: 'insert',
        insertPosition: 'start',
      })

      // LISTING ['tags', sortProp, sortDir]: On CREATE → refetch listing queries so ordering/pagination is correct
      this.removeTagListings()

      // Optionally keep invalidation for full listing pages
      this.router.navigate(['/tag', data['id']], { replaceUrl: true })
    },
  }))

  mutationUpdateTag = injectMutation(() => ({
    mutationFn: async () => {
      this.submitted.set(true)
      // this.getDirtyValues() // todo ???
      const current = this.tagForm.getRawValue() as any
      const initial = this.initialFormValue() as any
      const id = this.queryTag.data()?.id
      const n = this.payloadBuilder.buildPatch(current, initial, {
        id,
        required: ['name', 'type', 'color', 'externalId'],
        optional: {
          name: {},
          type: {},
          color: {},
          externalId: {},
        },
      })

      return await this.tagService.updateTag(n, tagSchema)
    },
    onSuccess: data => {
      this.submitted.set(false)
      // Snapshot current form as baseline and mark pristine so guard won't prompt right after save
      const current = this.tagForm.getRawValue()
      this.initialFormValue.set(structuredClone(current))
      this.tagForm.markAsPristine()

      this.notificationService.success(this.translateService.instant('alert.tag.updated'))

      // Update the detail cache for this tag with returned server data
      this.queryClient.setQueryData(['tag', data['id']], data)

      // OPTIONS: keep as-is → update options cache value for this tag
      updateCachedListQueries(this.queryClient, ['tags', 'options'], data, { mode: 'update' })

      // LISTING ['tags', sortProp, sortDir]: On UPDATE → if id is present in cached pages, refetch that exact query; otherwise do nothing
      this.removeTagListings()
    },
    enabled: !!this.router.url.split('/')[2],
  }))

  mutationDelete = injectMutation(() => ({
    mutationFn: async () => {
      this.getDirtyValues()
      await this.tagService.deleteTag({ id: Number(this.router.url.split('/')[2]) }).then(result => {
        this.notificationService.success(this.translateService.instant('alert.tag.deleted'))
        const idStr = String(this.router.url.split('/')[2])
        // OPTIONS: keep as-is → update options cache by removing the tag
        updateCachedListQueries(this.queryClient, ['tags', 'options'], { id: Number(idStr) } as any, {
          mode: 'delete',
        })

        // LISTING ['tags', sortProp, sortDir]: On DELETE → if id is present in cached pages, remove it; otherwise do nothing
        this.removeFromListingsIfPresent(Number(idStr))
        // Optionally refresh listing pages
        this.router.navigate(['/tags'], { replaceUrl: true })
      })
    },
    enabled: !!this.router.url.split('/')[2],
  }))

  readonly queryTagTypes = injectQuery(() => ({
    queryKey: ['tagTypes', 'options'],
    queryFn: async () => {
      return await this.tagService.getTypes({}, { id: null }).then(result => {
        return result.items.map(item => item.id)
      })
    },
  }))

  isCreating = computed(() => !this.route.snapshot.params['id'])

  loading = computed(() => {
    return this.queryTag.isFetching() || this.mutationCreateTag.isPending() || this.mutationUpdateTag.isPending()
  })

  constructor(
    public router: Router,
    private route: ActivatedRoute,
    private notificationService: NotificationService,
    private translateService: TranslateService,
    private location: Location
  ) {
    effect(() => {
      const d = this.queryTag.data()
      const id = this.route.snapshot.params['id']
      if (id && d) {
        this.fillFormValue(d)
      }
    })
    //  todo remove: Avoid globally disabling route reuse here; it can slow down back navigations/popstate.
    // this.router.routeReuseStrategy.shouldReuseRoute = () => false
  }

  public fillFormValue(tag) {
    const value = {
      name: tag.name ?? '',
      type: tag.type ?? '',
      color: tag.color ?? '',
      externalId: tag.externalId ?? '',
    }

    this.tagForm.setValue(value)
    // Store initial values snapshot and reset form dirty state
    this.initialFormValue.set(structuredClone(value))
    this.tagForm.markAsPristine()
  }

  // todo put this function into service to call it everywhere
  getDirtyValues(form = this.tagForm) {
    const dirtyValues = {}

    Object.keys(form.controls).forEach(key => {
      const currentControl = form.controls[key]

      if (currentControl.dirty) {
        if (currentControl.controls) dirtyValues[key] = this.getDirtyValues(currentControl)
        else dirtyValues[key] = currentControl.value
      }
    })

    return dirtyValues
  }

  getDataToSave() {
    const current = this.tagForm.getRawValue() as any
    const initial = this.initialFormValue() as any
    const patch: any = {}

    // Include id when editing
    // const routeId = this.route.snapshot.params['id']
    const routeId = this.queryTag.data()?.id
    if (routeId) patch.id = Number(routeId)

    // Always include required fields
    patch.name = current.name
    patch.type = current.type
    patch.color = current.color
    patch.externalId = current.externalId

    // Optional fields – include only when changed against initial snapshot
    const norm = (v: any) => (v === undefined || v === null ? '' : v)
    const idArray = (arr: any[]) =>
      Array.isArray(arr) ? arr.map((x: any) => x?.id).filter((v: any) => v !== undefined && v !== null) : []
    const eqArrayIds = (a: any[], b: any[]) => {
      const aa = idArray(a)
        .slice()
        .sort((x, y) => (x ?? 0) - (y ?? 0))
      const bb = idArray(b)
        .slice()
        .sort((x, y) => (x ?? 0) - (y ?? 0))
      if (aa.length !== bb.length) return false
      for (let i = 0; i < aa.length; i++) if (aa[i] !== bb[i]) return false
      return true
    }

    if (norm(current.name) !== norm(initial?.name)) {
      patch.name = current.name
    }
    if (norm(current.type) !== norm(initial?.type)) {
      patch.type = current.type
    }
    if (norm(current.color) !== norm(initial?.color)) {
      patch.color = current.color
    }
    if (norm(current.externalId) !== norm(initial?.externalId)) {
      patch.externalId = current.externalId
    }

    return patch
  }

  hasUnsavedDataChanges(): boolean {
    const current = this.tagForm.getRawValue() as any
    const initial = this.initialFormValue()

    // If initial is not yet set (e.g., brand-new form), fall back to form dirty flag
    if (!initial) {
      return this.tagForm.dirty
    }

    const norm = (v: any) => (v === undefined || v === null ? '' : String(v))

    const changed =
      norm(current?.name) !== norm(initial.name) ||
      norm(current?.type) !== norm(initial.type) ||
      norm(current?.color) !== norm(initial.color) ||
      norm(current?.externalId) !== norm(initial.externalId)

    return changed
  }

  goBack(): void {
    // todo remove later if safe: : Use Angular Location to go back in history while staying within the Router pipeline.
    // historyBack()
    this.location.back()
  }

  get formControls(): { [key: string]: AbstractControl } {
    return this.tagForm.controls
  }

  // ===== Helpers for cache handling of tag listings =====
  private isTagsListingKey(key: unknown[]): boolean {
    if (!Array.isArray(key)) return false
    if (key.length !== 3) return false
    return key[0] === 'tags' && typeof key[1] === 'string' && typeof key[2] === 'string'
  }

  private listingContainsId(cached: any, id: number | string): boolean {
    const idStr = String(id)
    if (!cached || !Array.isArray(cached.pages)) return false
    for (const page of cached.pages) {
      const items = Array.isArray(page) ? page : Array.isArray(page?.items) ? page.items : undefined
      if (!Array.isArray(items)) {
        continue
      }
      if (items.some((it: any) => String(it?.id) === idStr)) {
        return true
      }
    }
    return false
  }

  private removeIdFromCachedListing(key: unknown[], id: number | string): void {
    const cached: any = this.queryClient.getQueryData(key as any)
    if (!cached || !Array.isArray(cached.pages)) return
    const idStr = String(id)
    const newPages = cached.pages.map((page: any) => {
      if (Array.isArray(page)) {
        return page.filter((it: any) => String(it?.id) !== idStr)
      }
      if (Array.isArray(page?.items)) {
        return { ...page, items: page.items.filter((it: any) => String(it?.id) !== idStr) }
      }
      return page
    })
    this.queryClient.setQueryData(key as any, { ...cached, pages: newPages })
  }

  // CREATE → refetch all cached tag listings (['tags', sortProp, sortDir])
  private removeTagListings(): void {
    const matches = this.queryClient.getQueriesData<any>({ queryKey: ['tags'] }) as Array<[unknown[], any]>
    for (const [key, cached] of matches) {
      this.queryClient.removeQueries({ queryKey: key as any, exact: true })
    }
  }

  // DELETE → if present in cached listings, remove it; otherwise do nothing
  private removeFromListingsIfPresent(id: number | string): void {
    const matches = this.queryClient.getQueriesData<any>({ queryKey: ['tags'] }) as Array<[unknown[], any]>
    for (const [key, cached] of matches) {
      if (!this.isTagsListingKey(key as unknown[])) continue
      if (!this.listingContainsId(cached, id)) continue
      this.removeIdFromCachedListing(key as unknown[], id)
    }
  }
}
